import { useState } from "react";
import { useMutation } from "@apollo/client/react";
import { FormattedMessage, useIntl } from "react-intl";
import { Button } from "@/components/design/Button";
import { useValidationFallback } from "@/components/match/use-match-editor";
import { UpdateListingDocument } from "@/graphql/generated/operations";
import { userErrorMessage } from "@/lib/user-error";
import { groupLabel } from "@/pages/settings/access-group-labels";
import { ListingField } from "./ListingField";
import { ListingForm } from "./ListingForm";
import { formValuesOf, updateListingData, throwIfRejected, ValidationRejected } from "./listing-form";
import { forgetListings, type ListingDetail } from "./use-listing";

export function ListingReadershipField({ listing, label }: { listing: ListingDetail; label: string }) {
  const intl = useIntl();
  const [editing, setEditing] = useState(false);
  // Leserschaft ist Inhalt (WRITE auf `plan`), nicht Zugriffsverwaltung: genesis verlangt für `sharedWith`
  // und `publiclyReadable` dasselbe wie für den Text.
  const canEdit = listing.actions.canUpdate;
  const summary = listing.publiclyReadable ? <FormattedMessage id="share.listings.readershipPublic" />
    : listing.sharedWith?.length ? listing.sharedWith.map(group => groupLabel(intl, group)).join(", ")
    : <FormattedMessage id="share.listings.readershipMembers" />;
  return <div className="min-w-0">
    <ListingField label={label}>
      {editing && canEdit ? <ReadershipEditor listing={listing} onClose={() => setEditing(false)} /> : <>
        {canEdit ? <button type="button" id="listing-edit-readership" aria-expanded={false}
          onClick={() => setEditing(true)}
          className="rounded border-b border-dotted border-ink/30 text-left hover:bg-ink/5 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ink/30">{summary}</button> : summary}
        <p className="smycles-field-meta mt-2 text-ink-muted"><FormattedMessage id="share.listings.readershipHint" /></p>
      </>}
    </ListingField>
  </div>;
}

function ReadershipEditor({ listing, onClose }: { listing: ListingDetail; onClose: () => void }) {
  const intl = useIntl();
  const fallback = useValidationFallback();
  const [initial] = useState(() => formValuesOf(listing));
  const [values, setValues] = useState(initial);
  const [error, setError] = useState<string | null>(null);
  const [update, { loading }] = useMutation(UpdateListingDocument);
  const data = updateListingData(initial, values);
  const changed = Object.keys(data).length > 0;
  async function save() {
    if (loading || !changed) return;
    setError(null);
    try {
      const result = await update({ variables: { id: listing.id, input: { data, dryRun: false } }, update: forgetListings });
      throwIfRejected(result.data?.updateListing, fallback);
      onClose();
    } catch (e) { setError(e instanceof ValidationRejected ? e.message : userErrorMessage(intl, e)); }
  }
  return <div className="max-w-2xl space-y-4">
    <fieldset disabled={loading}>
      <ListingForm section="readership" values={values} onChange={setValues} retentionWasSet={initial.retentionDays !== ""}
        knownRecipients={listing.sharedWith ?? []} knownCategories={listing.categories} />
    </fieldset>
    {error ? <p role="alert" className="smycles-field-meta text-ink">{error}</p> : null}
    <div className="flex flex-wrap gap-2">
      <Button id="listing-save-readership" size="compact" disabled={loading || !changed} onClick={() => void save()}>
        <FormattedMessage id={loading ? "common.saving" : "common.save"} />
      </Button>
      <Button id="listing-cancel-readership" variant="secondary" size="compact" disabled={loading} onClick={onClose}>
        <FormattedMessage id="common.cancel" />
      </Button>
    </div>
  </div>;
}
